import React, {
    useEffect,
    useRef,
    useState,
} from "react";

import {
    FaGraduationCap,
    FaHeart,
    FaLightbulb,
    FaUsers,
    FaBookOpen,
    FaAward,
    FaStar,
    FaBullseye,
    FaEye,
    FaCheckCircle,
    FaBolt,
    FaBriefcase,
    FaLaptopCode,
    FaUserTie,
    FaArrowRight,
    FaCertificate,
    FaHandsHelping,
    FaBalanceScale,
    FaChartLine,
    FaGlobeAfrica,
    FaUserGraduate,
    FaBuilding,
    FaPeopleCarry,
} from "react-icons/fa";

import bgImage from "./passionaids.jpg";

const About = () => {
    const [visibleSections, setVisibleSections] = useState([]);
    const sectionRefs = useRef([]);

    useEffect(() => {
        const observers = [];

        sectionRefs.current.forEach((section, index) => {
            if (!section) return;

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            setVisibleSections((previous) =>
                                previous.includes(index)
                                    ? previous
                                    : [...previous, index]
                            );

                            observer.unobserve(entry.target);
                        }
                    });
                },
                {
                    threshold: 0.12,
                }
            );

            observer.observe(section);
            observers.push(observer);
        });

        return () => {
            observers.forEach((observer) =>
                observer.disconnect()
            );
        };
    }, []);

    const registerSection = (element) => {
        if (
            element &&
            !sectionRefs.current.includes(element)
        ) {
            sectionRefs.current.push(element);
        }
    };

    return (
        <main className="patif-about-page">

            {/* =========================================================
                HERO
            ========================================================= */}

            <section
                className="patif-about-hero"
                style={{
                    backgroundImage: `url(${bgImage})`,
                }}
            >
                <div className="patif-hero-overlay" />

                <div className="patif-hero-grid" />

                <div className="patif-hero-accent accent-one" />
                <div className="patif-hero-accent accent-two" />

                <div className="patif-hero-content">

                    <div className="patif-hero-icon">
                        <FaHeart />
                    </div>

                    <div className="patif-hero-label">
                        <span />
                        PASSION • EDUCATION • OPPORTUNITY
                        <span />
                    </div>

                    <h1>
                        Passion That
                        <br />
                        <span>Builds The Future.</span>
                    </h1>

                    <p>
                        PASSION AIDS THE FUTURE is committed to
                        expanding access to practical education,
                        professional development, vocational skills,
                        leadership, entrepreneurship, compliance,
                        justice, and community empowerment.
                    </p>

                    <div className="patif-hero-buttons">

                        <a
                            href="#our-story"
                            className="patif-primary-button"
                        >
                            Discover Our Institution
                            <FaArrowRight />
                        </a>

                        <a
                            href="#mission"
                            className="patif-secondary-button"
                        >
                            Our Mission
                        </a>

                    </div>

                    <div className="patif-hero-features">

                        <div>
                            <FaGraduationCap />
                            <span>
                                Professional Education
                            </span>
                        </div>

                        <div>
                            <FaBriefcase />
                            <span>
                                Practical Skills
                            </span>
                        </div>

                        <div>
                            <FaUsers />
                            <span>
                                Community Empowerment
                            </span>
                        </div>

                    </div>
                </div>

                <div className="patif-scroll-indicator">
                    <span />
                    <small>
                        SCROLL TO EXPLORE
                    </small>
                </div>
            </section>

            {/* =========================================================
                INTRODUCTION
            ========================================================= */}

            <section
                id="our-story"
                className={`patif-introduction ${
                    visibleSections.includes(0)
                        ? "patif-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >
                <div className="patif-intro-container">

                    <div className="patif-intro-image">

                        <div className="patif-intro-image-frame">

                            <img
                                src={bgImage}
                                alt="PASSION AIDS THE FUTURE"
                            />

                            <div className="patif-image-overlay" />

                            <div className="patif-image-corner top-left" />
                            <div className="patif-image-corner bottom-right" />

                            <div className="patif-image-badge">

                                <FaHeart />

                                <div>
                                    <strong>
                                        Passion
                                    </strong>

                                    <span>
                                        With Purpose
                                    </span>
                                </div>

                            </div>

                            <div className="patif-image-number">
                                <span>01</span>
                            </div>

                        </div>
                    </div>

                    <div className="patif-intro-content">

                        <span className="patif-section-label">
                            WHO WE ARE
                        </span>

                        <h2>
                            Turning Passion Into
                            <span>
                                Opportunity.
                            </span>
                        </h2>

                        <div className="patif-divider">
                            <span />
                            <span />
                        </div>

                        <p>
                            PASSION AIDS THE FUTURE is an
                            institution dedicated to providing
                            accessible, practical, and professional
                            education that empowers individuals to
                            develop knowledge, skills, leadership
                            capacity, and professional competencies.
                        </p>

                        <p>
                            We believe education should do more than
                            transfer information. It should help
                            individuals discover their potential,
                            develop practical abilities, become
                            responsible leaders, create opportunities,
                            and contribute meaningfully to their
                            communities.
                        </p>

                        <p>
                            Through professional education, vocational
                            training, leadership development,
                            entrepreneurship, compliance, justice,
                            and community-focused programs, we work
                            to connect learning with real opportunity
                            and sustainable development.
                        </p>

                        <div className="patif-intro-highlights">

                            <div>
                                <FaCheckCircle />
                                <span>
                                    Professional Education
                                </span>
                            </div>

                            <div>
                                <FaCheckCircle />
                                <span>
                                    Practical Skills
                                </span>
                            </div>

                            <div>
                                <FaCheckCircle />
                                <span>
                                    Leadership Development
                                </span>
                            </div>

                            <div>
                                <FaCheckCircle />
                                <span>
                                    Community Empowerment
                                </span>
                            </div>

                        </div>
                    </div>
                </div>
            </section>

            {/* =========================================================
                MISSION / VISION
            ========================================================= */}

            <section
                id="mission"
                className={`patif-purpose-section ${
                    visibleSections.includes(1)
                        ? "patif-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >

                <div className="patif-section-heading">

                    <span className="patif-section-label">
                        OUR PURPOSE
                    </span>

                    <h2>
                        Education With
                        <span>
                            Purpose.
                        </span>
                    </h2>

                    <p>
                        We connect education, practical skills,
                        leadership, and opportunity to help people
                        build better futures.
                    </p>

                </div>

                <div className="patif-purpose-grid">

                    <div className="patif-purpose-card mission">

                        <div className="patif-purpose-top">

                            <div className="patif-purpose-icon">
                                <FaBullseye />
                            </div>

                            <span>
                                01
                            </span>

                        </div>

                        <div className="patif-purpose-label">
                            OUR MISSION
                        </div>

                        <h3>
                            Empowering People Through Education
                        </h3>

                        <p>
                            To provide accessible, inclusive, and
                            quality professional and vocational
                            education that equips individuals with
                            knowledge, practical skills, ethical
                            values, leadership capacity, and the
                            confidence to succeed, create opportunities,
                            and contribute to sustainable development.
                        </p>

                        <div className="patif-purpose-bottom">

                            <FaGraduationCap />

                            <span>
                                Education • Skills • Leadership
                            </span>

                        </div>

                    </div>

                    <div className="patif-purpose-card vision">

                        <div className="patif-purpose-top">

                            <div className="patif-purpose-icon">
                                <FaEye />
                            </div>

                            <span>
                                02
                            </span>

                        </div>

                        <div className="patif-purpose-label">
                            OUR VISION
                        </div>

                        <h3>
                            A Future Built Through Opportunity
                        </h3>

                        <p>
                            To become a leading institution for
                            professional education, vocational
                            training, leadership development,
                            entrepreneurship, and community
                            empowerment, creating pathways through
                            which individuals and communities can
                            achieve sustainable progress.
                        </p>

                        <div className="patif-purpose-bottom">

                            <FaGlobeAfrica />

                            <span>
                                Opportunity • Growth • Impact
                            </span>

                        </div>

                    </div>

                </div>
            </section>

            {/* =========================================================
                PHILOSOPHY
            ========================================================= */}

            <section
                className={`patif-approach-section ${
                    visibleSections.includes(2)
                        ? "patif-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >

                <div className="patif-approach-container">

                    <div className="patif-approach-content">

                        <span className="patif-section-label">
                            OUR PHILOSOPHY
                        </span>

                        <h2>
                            Passion Becomes
                            <span>
                                Possibility.
                            </span>
                        </h2>

                        <div className="patif-divider">
                            <span />
                            <span />
                        </div>

                        <p>
                            We believe every person carries potential
                            that can become meaningful when supported
                            by education, practical skills, leadership,
                            guidance, and opportunity.
                        </p>

                        <p>
                            Our philosophy is built around a simple
                            progression: passion should inspire
                            learning, learning should develop skills,
                            skills should create opportunity, and
                            opportunity should produce positive
                            individual and community impact.
                        </p>

                        <div className="patif-process">

                            <div className="patif-process-item">

                                <div className="patif-process-number">
                                    01
                                </div>

                                <div>
                                    <h4>
                                        Passion
                                    </h4>

                                    <p>
                                        Discover interests, purpose,
                                        ambition, and personal potential.
                                    </p>
                                </div>

                            </div>

                            <div className="patif-process-item">

                                <div className="patif-process-number">
                                    02
                                </div>

                                <div>
                                    <h4>
                                        Education
                                    </h4>

                                    <p>
                                        Build knowledge through relevant
                                        and meaningful learning.
                                    </p>
                                </div>

                            </div>

                            <div className="patif-process-item">

                                <div className="patif-process-number">
                                    03
                                </div>

                                <div>
                                    <h4>
                                        Skills
                                    </h4>

                                    <p>
                                        Transform knowledge into practical
                                        and professional capability.
                                    </p>
                                </div>

                            </div>

                            <div className="patif-process-item">

                                <div className="patif-process-number">
                                    04
                                </div>

                                <div>
                                    <h4>
                                        Opportunity
                                    </h4>

                                    <p>
                                        Use knowledge and skills to create
                                        meaningful professional and social
                                        opportunities.
                                    </p>
                                </div>

                            </div>

                        </div>
                    </div>

                    <div className="patif-approach-visual">

                        <div className="patif-visual-main">

                            <div className="patif-visual-icon">
                                <FaLightbulb />
                            </div>

                            <span>
                                THE PATIF PHILOSOPHY
                            </span>

                            <h3>
                                Passion.
                                <strong>
                                    Education.
                                </strong>
                                Skills.
                            </h3>

                            <p>
                                Developing people who are prepared
                                to learn, lead, work, create, serve,
                                and make a difference.
                            </p>

                            <div className="patif-visual-tools">

                                <span>
                                    <FaGraduationCap />
                                </span>

                                <span>
                                    <FaHandsHelping />
                                </span>

                                <span>
                                    <FaLaptopCode />
                                </span>

                                <span>
                                    <FaBriefcase />
                                </span>

                            </div>

                        </div>

                        <div className="patif-visual-small-card">

                            <FaCertificate />

                            <div>
                                <strong>
                                    Purpose-Driven Education
                                </strong>

                                <span>
                                    Learning for real impact
                                </span>
                            </div>

                        </div>

                    </div>

                </div>
            </section>

            {/* =========================================================
                CORE VALUES
            ========================================================= */}

            <section
                className={`patif-values-section ${
                    visibleSections.includes(3)
                        ? "patif-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >

                <div className="patif-section-heading">

                    <span className="patif-section-label">
                        WHAT WE BELIEVE
                    </span>

                    <h2>
                        Our Core
                        <span>
                            Values.
                        </span>
                    </h2>

                    <p>
                        Our values guide how we educate, lead,
                        communicate, serve, and create opportunities
                        for others.
                    </p>

                </div>

                <div className="patif-values-grid">

                    <div className="patif-value-card">

                        <div className="patif-value-number">
                            01
                        </div>

                        <div className="patif-value-icon">
                            <FaBookOpen />
                        </div>

                        <h3>
                            Excellence
                        </h3>

                        <p>
                            We pursue high standards in education,
                            professional development, practical
                            learning, leadership, and service.
                        </p>

                    </div>

                    <div className="patif-value-card">

                        <div className="patif-value-number">
                            02
                        </div>

                        <div className="patif-value-icon">
                            <FaHeart />
                        </div>

                        <h3>
                            Integrity
                        </h3>

                        <p>
                            We promote honesty, accountability,
                            responsibility, respect, fairness, and
                            ethical conduct.
                        </p>

                    </div>

                    <div className="patif-value-card">

                        <div className="patif-value-number">
                            03
                        </div>

                        <div className="patif-value-icon">
                            <FaLightbulb />
                        </div>

                        <h3>
                            Innovation
                        </h3>

                        <p>
                            We encourage creativity, critical thinking,
                            new ideas, technology, and practical
                            solutions to real challenges.
                        </p>

                    </div>

                    <div className="patif-value-card">

                        <div className="patif-value-number">
                            04
                        </div>

                        <div className="patif-value-icon">
                            <FaUsers />
                        </div>

                        <h3>
                            Collaboration
                        </h3>

                        <p>
                            We believe meaningful progress is achieved
                            through communication, teamwork, partnership,
                            and mutual respect.
                        </p>

                    </div>

                    <div className="patif-value-card">

                        <div className="patif-value-number">
                            05
                        </div>

                        <div className="patif-value-icon">
                            <FaAward />
                        </div>

                        <h3>
                            Professionalism
                        </h3>

                        <p>
                            We develop discipline, competence,
                            reliability, confidence, responsibility,
                            and respect for others.
                        </p>

                    </div>

                    <div className="patif-value-card">

                        <div className="patif-value-number">
                            06
                        </div>

                        <div className="patif-value-icon">
                            <FaStar />
                        </div>

                        <h3>
                            Human Potential
                        </h3>

                        <p>
                            We believe every individual can grow,
                            develop valuable abilities, and contribute
                            when given knowledge, guidance, and opportunity.
                        </p>

                    </div>

                </div>
            </section>

            {/* =========================================================
                WHAT PEOPLE GAIN
            ========================================================= */}

            <section
                className={`patif-benefits-section ${
                    visibleSections.includes(4)
                        ? "patif-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >

                <div className="patif-benefits-container">

                    <div className="patif-benefits-header">

                        <span className="patif-section-label">
                            WHAT WE PROVIDE
                        </span>

                        <h2>
                            More Than
                            <span>
                                Education.
                            </span>
                        </h2>

                        <p>
                            We seek to develop capable, confident,
                            responsible, and opportunity-ready people
                            who can contribute positively to society.
                        </p>

                    </div>

                    <div className="patif-benefits-grid">

                        <div className="patif-benefit">

                            <div className="patif-benefit-icon">
                                <FaGraduationCap />
                            </div>

                            <div>
                                <h3>
                                    Professional Education
                                </h3>

                                <p>
                                    Relevant education designed to
                                    strengthen knowledge, competence,
                                    and professional confidence.
                                </p>
                            </div>

                        </div>

                        <div className="patif-benefit">

                            <div className="patif-benefit-icon">
                                <FaBriefcase />
                            </div>

                            <div>
                                <h3>
                                    Career Development
                                </h3>

                                <p>
                                    Practical preparation that helps
                                    individuals navigate employment,
                                    professional growth, and opportunity.
                                </p>
                            </div>

                        </div>

                        <div className="patif-benefit">

                            <div className="patif-benefit-icon">
                                <FaLightbulb />
                            </div>

                            <div>
                                <h3>
                                    Entrepreneurship
                                </h3>

                                <p>
                                    Encouraging initiative, creativity,
                                    problem-solving, enterprise, and
                                    sustainable self-employment.
                                </p>
                            </div>

                        </div>

                        <div className="patif-benefit">

                            <div className="patif-benefit-icon">
                                <FaUserTie />
                            </div>

                            <div>
                                <h3>
                                    Leadership Development
                                </h3>

                                <p>
                                    Building responsible leaders who
                                    understand service, accountability,
                                    ethics, and community responsibility.
                                </p>
                            </div>

                        </div>

                        <div className="patif-benefit">

                            <div className="patif-benefit-icon">
                                <FaBalanceScale />
                            </div>

                            <div>
                                <h3>
                                    Compliance & Justice
                                </h3>

                                <p>
                                    Supporting knowledge and awareness
                                    around responsible conduct, compliance,
                                    justice, and ethical practice.
                                </p>
                            </div>

                        </div>

                        <div className="patif-benefit">

                            <div className="patif-benefit-icon">
                                <FaPeopleCarry />
                            </div>

                            <div>
                                <h3>
                                    Community Empowerment
                                </h3>

                                <p>
                                    Connecting education and opportunity
                                    with positive community development
                                    and meaningful social impact.
                                </p>
                            </div>

                        </div>

                    </div>
                </div>
            </section>

            {/* =========================================================
                HIGHLIGHT BAR
            ========================================================= */}

            <section className="patif-highlight-section">

                <div className="patif-highlight-overlay" />

                <div className="patif-highlight-container">

                    <div className="patif-highlight-item">

                        <FaGraduationCap />

                        <strong>
                            Education
                        </strong>

                        <span>
                            Knowledge
                        </span>

                    </div>

                    <div className="patif-highlight-item">

                        <FaBolt />

                        <strong>
                            Skills
                        </strong>

                        <span>
                            Practical Ability
                        </span>

                    </div>

                    <div className="patif-highlight-item">

                        <FaUserTie />

                        <strong>
                            Leadership
                        </strong>

                        <span>
                            Responsible Action
                        </span>

                    </div>

                    <div className="patif-highlight-item">

                        <FaChartLine />

                        <strong>
                            Opportunity
                        </strong>

                        <span>
                            Sustainable Growth
                        </span>

                    </div>

                </div>
            </section>

            {/* =========================================================
                CTA
            ========================================================= */}

            <section
                className={`patif-about-cta ${
                    visibleSections.includes(5)
                        ? "patif-section-visible"
                        : ""
                }`}
                ref={registerSection}
            >

                <div className="patif-cta-decoration decoration-one" />
                <div className="patif-cta-decoration decoration-two" />
                <div className="patif-cta-glow" />

                <div className="patif-cta-content">

                    <span className="patif-section-label">
                        THE FUTURE STARTS WITH PURPOSE
                    </span>

                    <h2>
                        Discover Your Passion.
                        <br />
                        <span>
                            Build Your Future.
                        </span>
                    </h2>

                    <p>
                        PASSION AIDS THE FUTURE connects education,
                        practical skills, leadership, entrepreneurship,
                        and opportunity to help individuals grow and
                        contribute meaningfully to their communities.
                    </p>

                    <div className="patif-cta-buttons">

                        <a
                            href="#courses"
                            className="patif-cta-primary"
                        >
                            Explore Our Programs
                            <FaArrowRight />
                        </a>

                        <a
                            href="#contact"
                            className="patif-cta-secondary"
                        >
                            Contact Us
                        </a>

                    </div>

                </div>
            </section>

            {/* =========================================================
                PAGE STYLES
            ========================================================= */}

            <style>{`
                * {
                    box-sizing: border-box;
                }

                :root {
                    --patif-black: #070908;
                    --patif-black-soft: #101411;
                    --patif-black-light: #171c18;

                    --patif-green: #16803a;
                    --patif-green-dark: #0b5d2a;
                    --patif-green-bright: #20a84a;

                    --patif-lemon: #c8f23a;
                    --patif-lemon-bright: #b7f000;

                    --patif-yellow: #f5d32f;
                    --patif-yellow-bright: #ffe45a;

                    --patif-white: #ffffff;
                    --patif-off-white: #f8faf7;

                    --patif-text: #121613;
                    --patif-muted: #68706b;

                    --patif-border: #e3e9e4;

                    --patif-shadow:
                        0 20px 55px
                        rgba(0, 0, 0, 0.10);

                    --patif-transition:
                        0.32s cubic-bezier(
                            0.22,
                            1,
                            0.36,
                            1
                        );
                }

                .patif-about-page {
                    width: 100%;
                    overflow: hidden;

                    background:
                        var(--patif-white);

                    color:
                        var(--patif-text);

                    font-family:
                        "Poppins",
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }

                /* =====================================================
                   GLOBAL
                ===================================================== */

                .patif-section-label {
                    display: inline-flex;

                    align-items: center;

                    color:
                        var(--patif-green);

                    font-size: 12px;

                    font-weight: 900;

                    letter-spacing: 2.5px;
                }

                .patif-divider {
                    display: flex;

                    align-items: center;

                    gap: 7px;

                    margin:
                        20px 0 26px;
                }

                .patif-divider span {
                    display: block;

                    height: 4px;

                    border-radius: 20px;
                }

                .patif-divider span:first-child {
                    width: 48px;

                    background:
                        var(--patif-green);
                }

                .patif-divider span:last-child {
                    width: 22px;

                    background:
                        var(--patif-lemon);
                }

                .patif-section-heading {
                    max-width: 800px;

                    margin:
                        0 auto 60px;

                    text-align: center;
                }

                .patif-section-heading h2 {
                    margin:
                        13px 0 16px;

                    color:
                        var(--patif-black);

                    font-size:
                        clamp(
                            36px,
                            4.5vw,
                            54px
                        );

                    line-height: 1.04;

                    font-weight: 900;

                    letter-spacing: -2px;
                }

                .patif-section-heading h2 span {
                    color:
                        var(--patif-green);
                }

                .patif-section-heading p {
                    max-width: 700px;

                    margin: 0 auto;

                    color:
                        var(--patif-muted);

                    font-size: 14px;

                    line-height: 1.85;
                }

                /* =====================================================
                   HERO
                ===================================================== */

                .patif-about-hero {
                    position: relative;

                    width: 100%;
                    min-height: 770px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    padding:
                        120px 25px 105px;

                    background-size: cover;

                    background-position: center;

                    isolation: isolate;

                    overflow: hidden;
                }

                /*
                    DARK OVERLAY RETAINED
                */

                .patif-hero-overlay {
                    position: absolute;

                    inset: 0;

                    z-index: -3;

                    background:
                        linear-gradient(
                            90deg,
                            rgba(
                                5,
                                7,
                                6,
                                0.96
                            ) 0%,
                            rgba(
                                5,
                                7,
                                6,
                                0.89
                            ) 38%,
                            rgba(
                                5,
                                7,
                                6,
                                0.72
                            ) 68%,
                            rgba(
                                5,
                                7,
                                6,
                                0.55
                            ) 100%
                        );
                }

                .patif-hero-grid {
                    position: absolute;

                    inset: 0;

                    z-index: -2;

                    opacity: 0.08;

                    background-image:
                        linear-gradient(
                            rgba(
                                255,
                                255,
                                255,
                                0.2
                            ) 1px,
                            transparent 1px
                        ),
                        linear-gradient(
                            90deg,
                            rgba(
                                255,
                                255,
                                255,
                                0.2
                            ) 1px,
                            transparent 1px
                        );

                    background-size:
                        60px 60px;
                }

                .patif-hero-accent {
                    position: absolute;

                    border-radius: 50%;

                    pointer-events: none;
                }

                .patif-hero-accent.accent-one {
                    width: 480px;
                    height: 480px;

                    top: -330px;
                    right: -150px;

                    border:
                        75px solid
                        rgba(
                            200,
                            242,
                            58,
                            0.07
                        );
                }

                .patif-hero-accent.accent-two {
                    width: 260px;
                    height: 260px;

                    left: -150px;
                    bottom: -150px;

                    background:
                        rgba(
                            22,
                            128,
                            58,
                            0.16
                        );
                }

                .patif-hero-content {
                    width: 100%;

                    max-width: 1180px;

                    margin: 0 auto;

                    color:
                        var(--patif-white);
                }

                .patif-hero-icon {
                    width: 70px;
                    height: 70px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    margin-bottom: 26px;

                    border:
                        2px solid
                        var(--patif-lemon);

                    border-radius: 17px;

                    background:
                        var(--patif-lemon);

                    color:
                        var(--patif-black);

                    font-size: 29px;

                    box-shadow:
                        0 15px 35px
                        rgba(
                            0,
                            0,
                            0,
                            0.3
                        );
                }

                .patif-hero-label {
                    display: flex;

                    align-items: center;

                    gap: 13px;

                    margin-bottom: 20px;

                    color:
                        var(--patif-lemon);

                    font-size: 12px;

                    font-weight: 900;

                    letter-spacing: 3px;
                }

                .patif-hero-label span {
                    width: 30px;
                    height: 2px;

                    background:
                        var(--patif-lemon);
                }

                .patif-hero-content h1 {
                    max-width: 900px;

                    margin: 0;

                    color:
                        var(--patif-white);

                    font-size:
                        clamp(
                            48px,
                            6.3vw,
                            82px
                        );

                    line-height: 0.98;

                    font-weight: 950;

                    letter-spacing: -4px;
                }

                .patif-hero-content h1 span {
                    color:
                        var(--patif-lemon);
                }

                .patif-hero-content > p {
                    max-width: 750px;

                    margin:
                        30px 0 0;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.82
                        );

                    font-size: 15px;

                    line-height: 1.9;
                }

                .patif-hero-buttons {
                    display: flex;

                    flex-wrap: wrap;

                    gap: 13px;

                    margin-top: 34px;
                }

                .patif-primary-button,
                .patif-secondary-button {
                    min-height: 54px;

                    display: inline-flex;

                    align-items: center;
                    justify-content: center;

                    gap: 10px;

                    padding:
                        0 23px;

                    border-radius: 9px;

                    text-decoration: none;

                    font-size: 13px;

                    font-weight: 850;

                    transition:
                        var(--patif-transition);
                }

                .patif-primary-button {
                    background:
                        var(--patif-lemon);

                    color:
                        var(--patif-black);

                    box-shadow:
                        0 12px 25px
                        rgba(
                            200,
                            242,
                            58,
                            0.18
                        );
                }

                .patif-primary-button:hover {
                    transform:
                        translateY(-4px);

                    background:
                        var(--patif-white);

                    color:
                        var(--patif-black);
                }

                .patif-secondary-button {
                    border:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.38
                        );

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.06
                        );

                    color:
                        var(--patif-white);
                }

                .patif-secondary-button:hover {
                    transform:
                        translateY(-4px);

                    background:
                        var(--patif-green);

                    border-color:
                        var(--patif-green);

                    color:
                        var(--patif-white);
                }

                .patif-hero-features {
                    display: flex;

                    flex-wrap: wrap;

                    gap: 28px;

                    margin-top: 47px;

                    padding-top: 25px;

                    border-top:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.18
                        );
                }

                .patif-hero-features div {
                    display: flex;

                    align-items: center;

                    gap: 10px;

                    color:
                        var(--patif-white);

                    font-size: 12px;

                    font-weight: 700;
                }

                .patif-hero-features svg {
                    color:
                        var(--patif-lemon);

                    font-size: 18px;
                }

                .patif-scroll-indicator {
                    position: absolute;

                    left: 50%;

                    bottom: 28px;

                    transform:
                        translateX(-50%);

                    display: flex;

                    flex-direction: column;

                    align-items: center;

                    gap: 8px;

                    color:
                        var(--patif-white);

                    opacity: 0.7;
                }

                .patif-scroll-indicator span {
                    width: 1px;
                    height: 30px;

                    background:
                        var(--patif-lemon);
                }

                .patif-scroll-indicator small {
                    font-size: 8px;

                    letter-spacing: 2.3px;

                    white-space: nowrap;
                }

                /* =====================================================
                   INTRODUCTION
                ===================================================== */

                .patif-introduction {
                    position: relative;

                    padding:
                        115px 25px;

                    background:
                        var(--patif-white);
                }

                .patif-intro-container {
                    width: 100%;

                    max-width: 1210px;

                    margin: 0 auto;

                    display: grid;

                    grid-template-columns:
                        minmax(0, 0.92fr)
                        minmax(0, 1.08fr);

                    gap: 82px;

                    align-items: center;
                }

                .patif-intro-image {
                    opacity: 0;

                    transform:
                        translateX(-50px);

                    transition:
                        opacity 0.8s ease,
                        transform 0.8s ease;
                }

                .patif-section-visible
                .patif-intro-image {
                    opacity: 1;

                    transform:
                        translateX(0);
                }

                .patif-intro-image-frame {
                    position: relative;

                    min-height: 550px;

                    padding: 14px;

                    background:
                        var(--patif-black);

                    border-radius: 21px;

                    box-shadow:
                        var(--patif-shadow);
                }

                .patif-intro-image-frame img {
                    width: 100%;
                    height: 100%;

                    min-height: 520px;

                    display: block;

                    object-fit: cover;

                    border-radius: 14px;
                }

                .patif-image-overlay {
                    position: absolute;

                    inset: 14px;

                    border-radius: 14px;

                    background:
                        linear-gradient(
                            to top,
                            rgba(
                                5,
                                7,
                                6,
                                0.72
                            ),
                            rgba(
                                5,
                                7,
                                6,
                                0.04
                            ) 65%
                        );
                }

                .patif-image-corner {
                    position: absolute;

                    width: 68px;
                    height: 68px;

                    border-color:
                        var(--patif-lemon);

                    border-style: solid;

                    z-index: 5;
                }

                .patif-image-corner.top-left {
                    top: -8px;
                    left: -8px;

                    border-width:
                        4px 0 0 4px;

                    border-radius:
                        15px 0 0 0;
                }

                .patif-image-corner.bottom-right {
                    right: -8px;
                    bottom: -8px;

                    border-width:
                        0 4px 4px 0;

                    border-radius:
                        0 0 15px 0;
                }

                .patif-image-badge {
                    position: absolute;

                    left: 34px;
                    bottom: 34px;

                    z-index: 8;

                    display: flex;

                    align-items: center;

                    gap: 13px;

                    padding:
                        15px 18px;

                    border:
                        1px solid
                        rgba(
                            200,
                            242,
                            58,
                            0.2
                        );

                    border-radius: 12px;

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.96
                        );

                    box-shadow:
                        0 15px 35px
                        rgba(
                            0,
                            0,
                            0,
                            0.22
                        );
                }

                .patif-image-badge > svg {
                    color:
                        var(--patif-green);

                    font-size: 25px;
                }

                .patif-image-badge div {
                    display: flex;

                    flex-direction: column;
                }

                .patif-image-badge strong {
                    color:
                        var(--patif-black);

                    font-size: 13px;
                }

                .patif-image-badge span {
                    color:
                        var(--patif-muted);

                    font-size: 10px;
                }

                .patif-image-number {
                    position: absolute;

                    top: 34px;
                    right: 34px;

                    z-index: 8;

                    width: 50px;
                    height: 50px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border-radius: 50%;

                    background:
                        var(--patif-lemon);

                    color:
                        var(--patif-black);

                    font-size: 12px;

                    font-weight: 950;

                    box-shadow:
                        0 8px 20px
                        rgba(
                            0,
                            0,
                            0,
                            0.25
                        );
                }

                .patif-intro-content {
                    opacity: 0;

                    transform:
                        translateX(50px);

                    transition:
                        opacity 0.8s ease 0.15s,
                        transform 0.8s ease 0.15s;
                }

                .patif-section-visible
                .patif-intro-content {
                    opacity: 1;

                    transform:
                        translateX(0);
                }

                .patif-intro-content h2 {
                    max-width: 650px;

                    margin:
                        13px 0 0;

                    color:
                        var(--patif-black);

                    font-size:
                        clamp(
                            36px,
                            4.2vw,
                            54px
                        );

                    line-height: 1.05;

                    font-weight: 900;

                    letter-spacing: -2px;
                }

                .patif-intro-content h2 span {
                    display: block;

                    color:
                        var(--patif-green);
                }

                .patif-intro-content p {
                    margin:
                        0 0 18px;

                    color:
                        var(--patif-muted);

                    font-size: 14px;

                    line-height: 1.9;
                }

                .patif-intro-highlights {
                    display: grid;

                    grid-template-columns:
                        1fr 1fr;

                    gap: 12px;

                    margin-top: 29px;
                }

                .patif-intro-highlights div {
                    display: flex;

                    align-items: center;

                    gap: 10px;

                    padding:
                        13px 14px;

                    border:
                        1px solid
                        var(--patif-border);

                    border-radius: 9px;

                    background:
                        #f8fbf7;

                    color:
                        var(--patif-text);

                    font-size: 11px;

                    font-weight: 750;
                }

                .patif-intro-highlights svg {
                    color:
                        var(--patif-green);

                    flex-shrink: 0;
                }

                /* =====================================================
                   PURPOSE
                ===================================================== */

                .patif-purpose-section {
                    padding:
                        115px 25px;

                    background:
                        #f3f7f3;
                }

                .patif-purpose-grid {
                    width: 100%;

                    max-width: 1080px;

                    margin: 0 auto;

                    display: grid;

                    grid-template-columns:
                        repeat(
                            2,
                            minmax(0, 1fr)
                        );

                    gap: 28px;
                }

                .patif-purpose-card {
                    position: relative;

                    padding:
                        37px;

                    min-height: 410px;

                    border-radius: 20px;

                    overflow: hidden;

                    transition:
                        var(--patif-transition);
                }

                .patif-purpose-card:hover {
                    transform:
                        translateY(-9px);

                    box-shadow:
                        0 25px 55px
                        rgba(
                            0,
                            0,
                            0,
                            0.15
                        );
                }

                .patif-purpose-card.mission {
                    background:
                        var(--patif-green);

                    color:
                        var(--patif-white);
                }

                .patif-purpose-card.vision {
                    background:
                        var(--patif-black);

                    color:
                        var(--patif-white);
                }

                .patif-purpose-card::after {
                    content: "";

                    position: absolute;

                    width: 240px;
                    height: 240px;

                    right: -110px;
                    bottom: -110px;

                    border-radius: 50%;

                    background:
                        rgba(
                            200,
                            242,
                            58,
                            0.08
                        );
                }

                .patif-purpose-top {
                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    margin-bottom: 35px;
                }

                .patif-purpose-icon {
                    width: 64px;
                    height: 64px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border-radius: 17px;

                    background:
                        var(--patif-lemon);

                    color:
                        var(--patif-black);

                    font-size: 24px;
                }

                .patif-purpose-top > span {
                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.22
                        );

                    font-size: 47px;

                    line-height: 1;

                    font-weight: 950;
                }

                .patif-purpose-label {
                    margin-bottom: 11px;

                    color:
                        var(--patif-lemon);

                    font-size: 11px;

                    font-weight: 900;

                    letter-spacing: 2px;
                }

                .patif-purpose-card h3 {
                    max-width: 460px;

                    margin:
                        0 0 17px;

                    font-size: 29px;

                    line-height: 1.18;

                    font-weight: 900;

                    letter-spacing: -0.7px;
                }

                .patif-purpose-card p {
                    max-width: 540px;

                    margin: 0;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.76
                        );

                    font-size: 13px;

                    line-height: 1.85;
                }

                .patif-purpose-bottom {
                    position: absolute;

                    left: 37px;
                    right: 37px;
                    bottom: 29px;

                    display: flex;

                    align-items: center;

                    gap: 9px;

                    padding-top: 17px;

                    border-top:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.16
                        );

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.78
                        );

                    font-size: 10px;

                    font-weight: 750;

                    letter-spacing: 0.5px;
                }

                .patif-purpose-bottom svg {
                    color:
                        var(--patif-lemon);
                }

                /* =====================================================
                   PHILOSOPHY
                ===================================================== */

                .patif-approach-section {
                    padding:
                        115px 25px;

                    background:
                        var(--patif-white);
                }

                .patif-approach-container {
                    width: 100%;

                    max-width: 1210px;

                    margin: 0 auto;

                    display: grid;

                    grid-template-columns:
                        minmax(0, 1fr)
                        minmax(0, 0.8fr);

                    gap: 85px;

                    align-items: center;
                }

                .patif-approach-content {
                    opacity: 0;

                    transform:
                        translateX(-50px);

                    transition:
                        opacity 0.8s ease,
                        transform 0.8s ease;
                }

                .patif-section-visible
                .patif-approach-content {
                    opacity: 1;

                    transform:
                        translateX(0);
                }

                .patif-approach-content h2 {
                    margin:
                        13px 0 0;

                    color:
                        var(--patif-black);

                    font-size:
                        clamp(
                            38px,
                            4.4vw,
                            57px
                        );

                    line-height: 1.03;

                    font-weight: 900;

                    letter-spacing: -2.2px;
                }

                .patif-approach-content h2 span {
                    color:
                        var(--patif-green);
                }

                .patif-approach-content > p {
                    margin:
                        0 0 17px;

                    color:
                        var(--patif-muted);

                    font-size: 14px;

                    line-height: 1.9;
                }

                .patif-process {
                    margin-top: 33px;
                }

                .patif-process-item {
                    display: grid;

                    grid-template-columns:
                        50px 1fr;

                    gap: 17px;

                    padding:
                        0 0 23px;

                    margin-bottom: 23px;

                    border-bottom:
                        1px solid
                        #e7ece8;
                }

                .patif-process-number {
                    width: 44px;
                    height: 44px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border-radius: 50%;

                    background:
                        var(--patif-green);

                    color:
                        var(--patif-white);

                    font-size: 11px;

                    font-weight: 950;
                }

                .patif-process-item:nth-child(2)
                .patif-process-number {
                    background:
                        var(--patif-lemon);

                    color:
                        var(--patif-black);
                }

                .patif-process-item:nth-child(4)
                .patif-process-number {
                    background:
                        var(--patif-black);

                    color:
                        var(--patif-lemon);
                }

                .patif-process-item h4 {
                    margin:
                        0 0 5px;

                    color:
                        var(--patif-black);

                    font-size: 17px;

                    font-weight: 900;
                }

                .patif-process-item p {
                    margin: 0;

                    color:
                        #737a75;

                    font-size: 12px;

                    line-height: 1.7;
                }

                .patif-approach-visual {
                    position: relative;

                    min-height: 540px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    opacity: 0;

                    transform:
                        translateX(50px);

                    transition:
                        opacity 0.8s ease 0.15s,
                        transform 0.8s ease 0.15s;
                }

                .patif-section-visible
                .patif-approach-visual {
                    opacity: 1;

                    transform:
                        translateX(0);
                }

                .patif-visual-main {
                    position: relative;

                    width: 400px;

                    min-height: 425px;

                    display: flex;

                    flex-direction: column;

                    align-items: center;
                    justify-content: center;

                    padding: 48px;

                    border-radius: 28px;

                    background:
                        linear-gradient(
                            145deg,
                            var(--patif-black),
                            #151b16
                        );

                    color:
                        var(--patif-white);

                    text-align: center;

                    box-shadow:
                        0 28px 65px
                        rgba(
                            0,
                            0,
                            0,
                            0.2
                        );

                    overflow: hidden;
                }

                .patif-visual-main::before {
                    content: "";

                    position: absolute;

                    inset: 12px;

                    border:
                        1px solid
                        rgba(
                            200,
                            242,
                            58,
                            0.25
                        );

                    border-radius: 21px;
                }

                .patif-visual-main::after {
                    content: "";

                    position: absolute;

                    width: 190px;
                    height: 190px;

                    top: -100px;
                    right: -90px;

                    border-radius: 50%;

                    background:
                        rgba(
                            200,
                            242,
                            58,
                            0.08
                        );
                }

                .patif-visual-icon {
                    width: 80px;
                    height: 80px;

                    position: relative;

                    z-index: 2;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    margin-bottom: 25px;

                    border-radius: 22px;

                    background:
                        var(--patif-lemon);

                    color:
                        var(--patif-black);

                    font-size: 32px;
                }

                .patif-visual-main > span {
                    position: relative;

                    z-index: 2;

                    color:
                        var(--patif-lemon);

                    font-size: 9px;

                    font-weight: 950;

                    letter-spacing: 2px;
                }

                .patif-visual-main h3 {
                    position: relative;

                    z-index: 2;

                    margin:
                        14px 0;

                    font-size: 34px;

                    line-height: 1.05;

                    font-weight: 900;
                }

                .patif-visual-main h3 strong {
                    display: block;

                    color:
                        var(--patif-lemon);
                }

                .patif-visual-main p {
                    position: relative;

                    z-index: 2;

                    max-width: 280px;

                    margin: 0;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.7
                        );

                    font-size: 12px;

                    line-height: 1.75;
                }

                .patif-visual-tools {
                    position: relative;

                    z-index: 2;

                    display: flex;

                    gap: 8px;

                    margin-top: 28px;
                }

                .patif-visual-tools span {
                    width: 38px;
                    height: 38px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border:
                        1px solid
                        rgba(
                            200,
                            242,
                            58,
                            0.16
                        );

                    border-radius: 10px;

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.06
                        );

                    color:
                        var(--patif-lemon);
                }

                .patif-visual-small-card {
                    position: absolute;

                    right: -5px;
                    bottom: 30px;

                    display: flex;

                    align-items: center;

                    gap: 11px;

                    padding:
                        15px 18px;

                    border:
                        1px solid
                        var(--patif-border);

                    border-radius: 12px;

                    background:
                        var(--patif-white);

                    box-shadow:
                        0 15px 35px
                        rgba(
                            0,
                            0,
                            0,
                            0.16
                        );
                }

                .patif-visual-small-card > svg {
                    color:
                        var(--patif-green);

                    font-size: 23px;
                }

                .patif-visual-small-card div {
                    display: flex;

                    flex-direction: column;
                }

                .patif-visual-small-card strong {
                    color:
                        var(--patif-black);

                    font-size: 11px;
                }

                .patif-visual-small-card span {
                    color:
                        var(--patif-muted);

                    font-size: 9px;
                }

                /* =====================================================
                   VALUES
                ===================================================== */

                .patif-values-section {
                    padding:
                        115px 25px;

                    background:
                        #f4f7f4;
                }

                .patif-values-grid {
                    width: 100%;

                    max-width: 1170px;

                    margin: 0 auto;

                    display: grid;

                    grid-template-columns:
                        repeat(
                            3,
                            minmax(0, 1fr)
                        );

                    gap: 20px;
                }

                .patif-value-card {
                    position: relative;

                    min-height: 285px;

                    padding: 31px;

                    border:
                        1px solid
                        var(--patif-border);

                    border-radius: 17px;

                    background:
                        var(--patif-white);

                    overflow: hidden;

                    transition:
                        var(--patif-transition);
                }

                .patif-value-card:hover {
                    transform:
                        translateY(-8px);

                    border-color:
                        rgba(
                            22,
                            128,
                            58,
                            0.25
                        );

                    box-shadow:
                        0 22px 45px
                        rgba(
                            0,
                            0,
                            0,
                            0.08
                        );
                }

                .patif-value-number {
                    position: absolute;

                    top: 18px;
                    right: 22px;

                    color:
                        rgba(
                            22,
                            128,
                            58,
                            0.09
                        );

                    font-size: 43px;

                    font-weight: 950;
                }

                .patif-value-icon {
                    width: 55px;
                    height: 55px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    margin-bottom: 22px;

                    border-radius: 14px;

                    background:
                        rgba(
                            22,
                            128,
                            58,
                            0.09
                        );

                    color:
                        var(--patif-green);

                    font-size: 21px;

                    transition:
                        var(--patif-transition);
                }

                .patif-value-card:hover
                .patif-value-icon {
                    background:
                        var(--patif-green);

                    color:
                        var(--patif-lemon);
                }

                .patif-value-card h3 {
                    margin:
                        0 0 10px;

                    color:
                        var(--patif-black);

                    font-size: 20px;

                    font-weight: 900;
                }

                .patif-value-card p {
                    margin: 0;

                    color:
                        #707771;

                    font-size: 12px;

                    line-height: 1.8;
                }

                /* =====================================================
                   BENEFITS
                ===================================================== */

                .patif-benefits-section {
                    padding:
                        110px 25px;

                    background:
                        var(--patif-white);
                }

                .patif-benefits-container {
                    width: 100%;

                    max-width: 1170px;

                    margin: 0 auto;
                }

                .patif-benefits-header {
                    max-width: 720px;

                    margin-bottom: 52px;
                }

                .patif-benefits-header h2 {
                    margin:
                        13px 0 16px;

                    color:
                        var(--patif-black);

                    font-size:
                        clamp(
                            37px,
                            4.4vw,
                            54px
                        );

                    line-height: 1.04;

                    font-weight: 900;

                    letter-spacing: -2px;
                }

                .patif-benefits-header h2 span {
                    color:
                        var(--patif-green);
                }

                .patif-benefits-header p {
                    margin: 0;

                    color:
                        var(--patif-muted);

                    font-size: 14px;

                    line-height: 1.85;
                }

                .patif-benefits-grid {
                    display: grid;

                    grid-template-columns:
                        repeat(
                            2,
                            minmax(0, 1fr)
                        );

                    gap: 18px;
                }

                .patif-benefit {
                    display: flex;

                    align-items: flex-start;

                    gap: 17px;

                    padding: 26px;

                    border:
                        1px solid
                        var(--patif-border);

                    border-radius: 15px;

                    background:
                        #f7faf7;

                    transition:
                        var(--patif-transition);
                }

                .patif-benefit:hover {
                    transform:
                        translateY(-5px);

                    background:
                        var(--patif-white);

                    border-color:
                        rgba(
                            22,
                            128,
                            58,
                            0.2
                        );

                    box-shadow:
                        0 18px 35px
                        rgba(
                            0,
                            0,
                            0,
                            0.06
                        );
                }

                .patif-benefit-icon {
                    width: 51px;
                    height: 51px;

                    flex-shrink: 0;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    border-radius: 13px;

                    background:
                        var(--patif-green);

                    color:
                        var(--patif-lemon);

                    font-size: 20px;
                }

                .patif-benefit h3 {
                    margin:
                        0 0 7px;

                    color:
                        var(--patif-black);

                    font-size: 17px;

                    font-weight: 900;
                }

                .patif-benefit p {
                    margin: 0;

                    color:
                        #707771;

                    font-size: 11.5px;

                    line-height: 1.75;
                }

                /* =====================================================
                   HIGHLIGHT
                ===================================================== */

                .patif-highlight-section {
                    position: relative;

                    padding:
                        70px 25px;

                    background:
                        var(--patif-black);

                    overflow: hidden;
                }

                /*
                    DARK AREA WITH LEMON-GREEN
                    AND WHITE TYPOGRAPHY
                */

                .patif-highlight-overlay {
                    position: absolute;

                    inset: 0;

                    background:
                        radial-gradient(
                            circle at 15% 50%,
                            rgba(
                                22,
                                128,
                                58,
                                0.34
                            ),
                            transparent 30%
                        ),
                        radial-gradient(
                            circle at 85% 50%,
                            rgba(
                                200,
                                242,
                                58,
                                0.10
                            ),
                            transparent 27%
                        );
                }

                .patif-highlight-container {
                    position: relative;

                    z-index: 2;

                    width: 100%;

                    max-width: 1080px;

                    margin: 0 auto;

                    display: grid;

                    grid-template-columns:
                        repeat(
                            4,
                            minmax(0, 1fr)
                        );
                }

                .patif-highlight-item {
                    display: flex;

                    flex-direction: column;

                    align-items: center;

                    justify-content: center;

                    padding:
                        12px 20px;

                    text-align: center;

                    border-right:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.13
                        );
                }

                .patif-highlight-item:last-child {
                    border-right: 0;
                }

                .patif-highlight-item svg {
                    margin-bottom: 11px;

                    color:
                        var(--patif-lemon);

                    font-size: 26px;
                }

                .patif-highlight-item strong {
                    color:
                        var(--patif-white);

                    font-size: 16px;

                    font-weight: 900;
                }

                .patif-highlight-item span {
                    margin-top: 4px;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.55
                        );

                    font-size: 10px;
                }

                /* =====================================================
                   CTA
                ===================================================== */

                .patif-about-cta {
                    position: relative;

                    padding:
                        120px 25px;

                    background:
                        linear-gradient(
                            135deg,
                            var(--patif-green-dark) 0%,
                            var(--patif-green) 55%,
                            #0c6d31 100%
                        );

                    overflow: hidden;

                    text-align: center;
                }

                .patif-cta-decoration {
                    position: absolute;

                    border-radius: 50%;

                    pointer-events: none;
                }

                .decoration-one {
                    width: 450px;
                    height: 450px;

                    top: -310px;
                    right: -130px;

                    border:
                        75px solid
                        rgba(
                            200,
                            242,
                            58,
                            0.10
                        );
                }

                .decoration-two {
                    width: 320px;
                    height: 320px;

                    bottom: -220px;
                    left: -130px;

                    background:
                        rgba(
                            0,
                            0,
                            0,
                            0.09
                        );
                }

                .patif-cta-glow {
                    position: absolute;

                    width: 300px;
                    height: 300px;

                    left: 50%;
                    top: 50%;

                    transform:
                        translate(
                            -50%,
                            -50%
                        );

                    border-radius: 50%;

                    background:
                        rgba(
                            200,
                            242,
                            58,
                            0.06 );

                    filter:
                        blur(10px);

                    pointer-events: none;
                }

                .patif-cta-content {
                    position: relative;

                    z-index: 5;

                    max-width: 820px;

                    margin: 0 auto;

                    opacity: 0;

                    transform:
                        translateY(35px);

                    transition:
                        opacity 0.8s ease,
                        transform 0.8s ease;
                }

                .patif-section-visible
                .patif-cta-content {
                    opacity: 1;

                    transform:
                        translateY(0);
                }

                .patif-cta-content
                .patif-section-label {
                    color:
                        var(--patif-lemon);
                }

                .patif-cta-content h2 {
                    margin:
                        15px 0 19px;

                    color:
                        var(--patif-white);

                    font-size:
                        clamp(
                            42px,
                            5.3vw,
                            68px
                        );

                    line-height: 1;

                    font-weight: 950;

                    letter-spacing: -2.7px;
                }

                .patif-cta-content h2 span {
                    color:
                        var(--patif-lemon);
                }

                .patif-cta-content > p {
                    max-width: 700px;

                    margin: 0 auto;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.78
                        );

                    font-size: 14px;

                    line-height: 1.85;
                }

                .patif-cta-buttons {
                    display: flex;

                    justify-content: center;

                    flex-wrap: wrap;

                    gap: 12px;

                    margin-top: 31px;
                }

                .patif-cta-primary,
                .patif-cta-secondary {
                    min-height: 54px;

                    display: inline-flex;

                    align-items: center;
                    justify-content: center;

                    gap: 10px;

                    padding:
                        0 24px;

                    border-radius: 9px;

                    text-decoration: none;

                    font-size: 12px;

                    font-weight: 900;

                    transition:
                        var(--patif-transition);
                }

                .patif-cta-primary {
                    background:
                        var(--patif-lemon);

                    color:
                        var(--patif-black);
                }

                .patif-cta-primary:hover {
                    transform:
                        translateY(-4px);

                    background:
                        var(--patif-white);
                }

                .patif-cta-secondary {
                    border:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.42
                        );

                    color:
                        var(--patif-white);

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.04
                        );
                }

                .patif-cta-secondary:hover {
                    transform:
                        translateY(-4px);

                    background:
                        var(--patif-black);

                    border-color:
                        var(--patif-black);
                }

                /* =====================================================
                   TABLET
                ===================================================== */

                @media (max-width: 1050px) {

                    .patif-intro-container {
                        gap: 48px;
                    }

                    .patif-approach-container {
                        gap: 48px;
                    }

                    .patif-values-grid {
                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0, 1fr)
                            );
                    }
                }

                /* =====================================================
                   MOBILE
                ===================================================== */

                @media (max-width: 760px) {

                    .patif-about-hero {
                        min-height: 720px;

                        padding:
                            105px 20px 80px;
                    }

                    .patif-hero-content h1 {
                        font-size: 48px;

                        letter-spacing: -2.5px;
                    }

                    .patif-hero-content > p {
                        font-size: 13px;

                        line-height: 1.8;
                    }

                    .patif-hero-features {
                        display: grid;

                        grid-template-columns:
                            1fr 1fr;

                        gap: 14px;
                    }

                    .patif-hero-features div:last-child {
                        grid-column:
                            span 2;
                    }

                    .patif-introduction,
                    .patif-purpose-section,
                    .patif-approach-section,
                    .patif-values-section,
                    .patif-benefits-section {
                        padding:
                            78px 18px;
                    }

                    .patif-intro-container,
                    .patif-approach-container {
                        grid-template-columns: 1fr;

                        gap: 52px;
                    }

                    .patif-intro-image-frame {
                        min-height: 410px;
                    }

                    .patif-intro-image-frame img {
                        min-height: 380px;
                    }

                    .patif-intro-highlights {
                        grid-template-columns: 1fr;
                    }

                    .patif-purpose-grid {
                        grid-template-columns: 1fr;
                    }

                    .patif-purpose-card {
                        min-height: 410px;

                        padding: 29px;
                    }

                    .patif-purpose-card h3 {
                        font-size: 26px;
                    }

                    .patif-approach-visual {
                        min-height: 450px;
                    }

                    .patif-visual-main {
                        width: 100%;

                        max-width: 400px;

                        min-height: 405px;
                    }

                    .patif-visual-small-card {
                        right: 0;

                        bottom: 8px;
                    }

                    .patif-values-grid {
                        grid-template-columns: 1fr;
                    }

                    .patif-value-card {
                        min-height: auto;
                    }

                    .patif-benefits-grid {
                        grid-template-columns: 1fr;
                    }

                    .patif-highlight-container {
                        grid-template-columns:
                            1fr 1fr;
                    }

                    .patif-highlight-item {
                        min-height: 115px;

                        border-right: 0;
                    }

                    .patif-highlight-item:nth-child(-n + 2) {
                        border-bottom:
                            1px solid
                            rgba(
                                255,
                                255,
                                255,
                                0.12
                            );
                    }

                    .patif-about-cta {
                        padding:
                            82px 18px;
                    }

                    .patif-cta-content h2 {
                        font-size: 44px;
                    }
                }

                /* =====================================================
                   SMALL MOBILE
                ===================================================== */

                @media (max-width: 450px) {

                    .patif-about-hero {
                        min-height: 700px;

                        padding-left: 16px;
                        padding-right: 16px;
                    }

                    .patif-hero-icon {
                        width: 59px;
                        height: 59px;

                        font-size: 24px;
                    }

                    .patif-hero-label {
                        font-size: 8px;

                        letter-spacing: 1.8px;

                        gap: 8px;
                    }

                    .patif-hero-label span {
                        width: 18px;
                    }

                    .patif-hero-content h1 {
                        font-size: 40px;

                        letter-spacing: -2px;
                    }

                    .patif-hero-buttons {
                        flex-direction: column;
                    }

                    .patif-primary-button,
                    .patif-secondary-button {
                        width: 100%;
                    }

                    .patif-hero-features {
                        grid-template-columns: 1fr;
                    }

                    .patif-hero-features div:last-child {
                        grid-column: auto;
                    }

                    .patif-intro-image-frame {
                        min-height: 350px;
                    }

                    .patif-intro-image-frame img {
                        min-height: 320px;
                    }

                    .patif-image-badge {
                        left: 24px;
                        bottom: 24px;
                    }

                    .patif-image-number {
                        top: 24px;
                        right: 24px;
                    }

                    .patif-section-heading h2,
                    .patif-intro-content h2 {
                        font-size: 35px;
                    }

                    .patif-purpose-bottom {
                        left: 29px;
                        right: 29px;
                    }

                    .patif-visual-main {
                        min-height: 360px;

                        padding:
                            31px 20px;
                    }

                    .patif-visual-main h3 {
                        font-size: 29px;
                    }

                    .patif-visual-small-card {
                        transform:
                            scale(0.88);

                        transform-origin:
                            bottom right;
                    }

                    .patif-highlight-container {
                        grid-template-columns: 1fr;
                    }

                    .patif-highlight-item {
                        min-height: 90px;

                        border-bottom:
                            1px solid
                            rgba(
                                255,
                                255,
                                255,
                                0.12
                            );
                    }

                    .patif-highlight-item:last-child {
                        border-bottom: 0;
                    }

                    .patif-cta-content h2 {
                        font-size: 38px;
                    }

                    .patif-cta-buttons {
                        flex-direction: column;
                    }

                    .patif-cta-primary,
                    .patif-cta-secondary {
                        width: 100%;
                    }
                }

                /* =====================================================
                   ACCESSIBILITY
                ===================================================== */

                .patif-about-page a:focus-visible {
                    outline:
                        3px solid
                        var(--patif-lemon);

                    outline-offset: 4px;
                }

                @media (prefers-reduced-motion: reduce) {

                    .patif-about-page *,
                    .patif-about-page *::before,
                    .patif-about-page *::after {
                        scroll-behavior: auto !important;

                        transition-duration:
                            0.01ms !important;

                        animation-duration:
                            0.01ms !important;

                        animation-iteration-count:
                            1 !important;
                    }
                }
            `}</style>
        </main>
    );
};

export default About;